import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, Play, CreditCard } from 'lucide-react';
import DownloadModal from './DownloadModal';

const plans = [
  {
    name: 'Mensuel',
    price: '6,99',
    period: '/mois',
    badge: null,
    features: [
      'Toutes les fonctionnalités',
      'Historique illimité',
      'Analyses détaillées',
      'Export PDF',
      'Résiliable à tout moment',
    ],
    highlight: false,
  },
  {
    name: 'Annuel',
    price: '67',
    period: '/an',
    subPrice: 'soit 5,58€/mois',
    badge: 'Économisez 20%',
    features: [
      'Tout le mensuel',
      'Priorité support',
      'Mises à jour en avant-première',
      'Export PDF',
    ],
    highlight: true,
  },
];

export default function Pricing() {
  const [modalOpen, setModalOpen] = useState(false);
  return (
    <section id="tarifs" data-testid="pricing-section" className="py-24 sm:py-32 relative">
      <DownloadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={'Vous avez un iPhone ou un Android\u00A0?'}
        subtitle="Choisissez votre appareil pour démarrer l'essai gratuit."
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-[#FF6B35] text-sm font-semibold uppercase tracking-widest font-['Poppins']"
          >
            Tarifs
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-white font-['Poppins'] font-bold text-3xl sm:text-4xl mt-3"
          >
            Simple et transparent
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-[#A0A0A0] mt-4 text-base"
          >
            Essai gratuit 7 jours &bull; Sans engagement &bull; Annulation à tout moment
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {plans.map((plan, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15, duration: 0.5 }}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              className={`relative rounded-2xl p-8 ${
                plan.highlight
                  ? 'gradient-border'
                  : 'bg-[#1A1A1A] border border-white/5'
              }`}
            >
              {plan.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white text-xs font-bold font-['Poppins']">
                  {plan.badge}
                </span>
              )}

              {plan.highlight && (
                <div className="absolute top-4 right-4 flex items-center gap-1 text-[#FF6B35]">
                  <Star size={14} fill="currentColor" />
                  <span className="text-xs font-semibold font-['Poppins']">Recommandé</span>
                </div>
              )}

              <h3 className="text-white font-['Poppins'] font-bold text-xl mb-4">{plan.name}</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-white font-['Poppins'] font-black text-5xl">{plan.price}</span>
                <span className="text-[#A0A0A0] text-lg">€{plan.period}</span>
              </div>
              {plan.subPrice && (
                <p className="text-[#FF6B35] text-sm font-medium mb-6">{plan.subPrice}</p>
              )}
              {!plan.subPrice && <div className="mb-6" />}

              <ul className="space-y-3 mb-8">
                {plan.features.map((f, j) => (
                  <li key={j} className="flex items-center gap-3 text-[#A0A0A0] text-sm">
                    <Check size={16} className="text-[#FF6B35] shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  data-testid={`pricing-trial-${plan.name.toLowerCase()}`}
                  className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-full font-semibold text-sm transition-all duration-300 ${
                    plan.highlight
                      ? 'bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white hover:shadow-[0_0_25px_rgba(255,107,53,0.3)] hover:scale-105'
                      : 'border border-white/10 text-white hover:bg-white/5 hover:border-[#FF6B35]/30'
                  }`}
                >
                  <Play size={16} fill="currentColor" />
                  Démarrer l&apos;essai gratuit
                </button>
                <button
                  type="button"
                  onClick={() => setModalOpen(true)}
                  data-testid={`pricing-subscribe-${plan.name.toLowerCase()}`}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-[#A0A0A0] text-xs font-medium hover:text-[#FF6B35] transition-colors duration-300"
                >
                  <CreditCard size={14} />
                  S&apos;abonner directement
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="text-center mt-8"
        >
          <p className="text-[#A0A0A0] text-xs">
            Paiement sécurisé par Stripe &bull; Sans carte bancaire pour l'essai
          </p>
          <div className="mt-2 flex justify-center gap-4">
            <a href="/cgu" data-testid="pricing-cgu-link" className="text-[#A0A0A0] text-xs hover:text-[#FF6B35] underline transition-colors">CGU</a>
            <a href="/politique-de-confidentialite" data-testid="pricing-privacy-link" className="text-[#A0A0A0] text-xs hover:text-[#FF6B35] underline transition-colors">Politique de confidentialité</a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
